// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function RecipesDisplay({ pending, recipes, checkBadge }: { pending: boolean; recipes: any[]; checkBadge: Record<string, { bg: string; color: string }> }) {
  const cell = { padding: "8px 10px", borderBottom: "1px solid #eee", verticalAlign: "top", fontSize: 12 };
  const hdr  = { ...cell, background: "#4285fb", color: "white", fontWeight: 600, fontSize: 11 };
  const ruleCell = { padding: "8px 10px", borderBottom: "1px solid #eee", verticalAlign: "top", fontSize: 12 };
  const ruleHdr  = { ...ruleCell, background: "#4285fb", color: "white", fontWeight: 600, fontSize: 11 };

  const rulesetsAndCascade = (
    <>
      <h3 style={{ color: "#333", marginTop: 0, marginBottom: 10 }}>Understanding Rulesets</h3>
      <p style={{ fontSize: 12, color: "#444", lineHeight: 1.6, marginBottom: 12 }}>
        A ruleset defines the minimum verification threshold an entity must meet. Recipes combine a ruleset with additional checks (AML screening, blocklist, duplicate detection, IDV, fraud).
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 20 }}>
        <thead>
          <tr>
            <th style={{ ...ruleHdr, width: "18%" }}>Ruleset</th>
            <th style={{ ...ruleHdr, width: "42%" }}>Requirement</th>
            <th style={{ ...ruleHdr, width: "40%" }}>When Used</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ background: "white" }}>
            <td style={{ ...ruleCell, fontWeight: 600 }}>1+1</td>
            <td style={ruleCell}>1 name match + 1 address or DOB match from at least 1 data source</td>
            <td style={ruleCell}>International or lower-assurance scenarios</td>
          </tr>
          <tr style={{ background: "#f9fafb" }}>
            <td style={{ ...ruleCell, fontWeight: 600 }}>2+2</td>
            <td style={ruleCell}>2 name matches + 2 address/DOB matches from at least 2 independent data sources (e.g. credit bureau + electoral roll)</td>
            <td style={ruleCell}>Australian Safe Harbour standard</td>
          </tr>
          <tr style={{ background: "white" }}>
            <td style={{ ...ruleCell, fontWeight: 600 }}>Gov ID</td>
            <td style={ruleCell}>Government-issued ID verified against DVS (Document Verification Service). Requires document details only — no physical ID photos</td>
            <td style={ruleCell}>Added on top of data source checks when government ID verification is required</td>
          </tr>
          <tr style={{ background: "#f9fafb" }}>
            <td style={{ ...ruleCell, fontWeight: 600 }}>Combined</td>
            <td style={ruleCell}>e.g. "2+2 with Gov ID" = 2 data points matched across 2 sources PLUS a government ID verified against DVS</td>
            <td style={ruleCell}>Higher assurance — standard for many Australian financial services</td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ color: "#333", marginTop: 24, marginBottom: 10 }}>Cascade (Data Source Selection)</h3>
      <div style={{ background: "#e8f0ff", borderLeft: "4px solid #4285fb", padding: "10px 14px", margin: "0 0 20px", borderRadius: "0 4px 4px 0", fontSize: 12, lineHeight: 1.7 }}>
        FrankieOne uses a <strong>cascade/sequential fallback</strong> approach for data sources:
        <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
          <li>A <strong>primary data source</strong> is tried first (e.g. a credit bureau like Equifax).</li>
          <li>If no match, the platform automatically tries a <strong>secondary source</strong> (e.g. Experian, electoral roll).</li>
          <li>This continues through all configured sources until a match is found or all sources are exhausted.</li>
        </ol>
        <p style={{ margin: "8px 0 0" }}>
          This increases pass rates — you <strong>only pay for sources actually used</strong>.
          Data source types include: government (DVS, passport databases), commercial (credit bureaus, electoral rolls, utility providers), and watchlist (sanctions, PEP, adverse media).
        </p>
      </div>
    </>
  );

  if (!recipes || recipes.length === 0) {
    return (
      <div>
        <h2 style={{ color: "#4285fb", borderBottom: "2px solid #4285fb", paddingBottom: 6, marginBottom: 16 }}>
          Recipes
        </h2>
        {rulesetsAndCascade}
        <p style={{ fontSize: 13, color: "#999", fontStyle: "italic" }}>No recipes configured for this API version yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ color: "#4285fb", borderBottom: "2px solid #4285fb", paddingBottom: 6, marginBottom: 16 }}>
        Recipes
      </h2>

      {rulesetsAndCascade}

      <h3 style={{ color: "#333", marginTop: 24, marginBottom: 10 }}>Configured Recipes</h3>
      <p style={{ fontSize: 13, color: "#444", marginBottom: 16, lineHeight: 1.6 }}>
        A recipe is a series of checks that run on an individual entity. Each recipe defines which check types
        to run and in what order. The check types listed below are executed in the order shown (left to right).
      </p>

      {pending && (
        <div style={{ background: "#fff8e1", border: "1px solid #f0c040", borderRadius: 4, padding: "8px 12px", marginBottom: 16, fontSize: 11, color: "#92400e" }}>
          <strong>In progress:</strong> Mandatory / Optional / Stop-on-fail details not yet added —
          paste them in the feedback box to complete this section.
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...hdr, width: "22%" }}>Recipe Name</th>
            <th className="api-docs-only" style={{ ...hdr, width: "18%" }}>Recipe Key</th>
            <th style={{ ...hdr, width: "60%" }}>Check Types (in execution order)</th>
          </tr>
        </thead>
        <tbody>
          {recipes.map((r, i) => (
            <tr key={r.key} style={{ background: i % 2 === 0 ? "white" : "#f9fafb" }}>
              <td style={{ ...cell, fontWeight: 600, color: "#4285fb" }}>{r.name}</td>
              <td className="api-docs-only" style={cell}>
                <code style={{ background: "#e8f0ff", padding: "2px 5px", borderRadius: 3, fontSize: 11, color: "#4285fb" }}>
                  {r.key}
                </code>
              </td>
              <td style={cell}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                  {r.checkTypes.map((ct: string, j: number) => {
                    const s = (checkBadge && checkBadge[ct]) || { bg: "#f3f4f6", color: "#374151" };
                    return (
                      <span key={j} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {j > 0 && <span style={{ color: "#bbb", fontSize: 10 }}>→</span>}
                        <span style={{ background: s.bg, color: s.color, padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
                          {ct}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: 11, color: "#999", marginTop: 10 }}>
        Mandatory / optional / stop-on-fail behaviour per check type will be documented once confirmed.
      </p>
    </div>
  );
}
